<template>
  <div ref="shell" class="student-map-shell" :class="{ 'has-student-dialog': dialogOpen }">
    <slot name="map" />
    <div v-if="ready" class="student-map-overlay" :style="overlayStyle">
      <div v-if="$slots.actions" class="student-map-global-actions"><slot name="actions" /></div>
      <div ref="dock" class="student-map-top student-map-bottom-dock" role="region" aria-label="实训操作栏">
        <div v-if="$slots.navigation" class="student-map-navigation" role="tablist" aria-label="实训导航"><slot name="navigation" /></div>
        <div v-if="$slots.top" class="student-map-quick-controls"><slot name="top" /></div>
      </div>
      <section v-if="listOpen" ref="listPanel" class="student-map-list" aria-label="地图列表" @pointerenter="onListPointerEnter" @pointerleave="onListPointerLeave" @pointerdown="onListPointerDown" @focusin="onListFocusIn" @focusout="onListFocusOut" @keydown="listKeyboardFocus = true">
        <header><div><b>{{ listTitle }}</b><small>{{ listDescription }}</small></div><div class="student-map-list-header-actions"><button v-if="autoScroll" type="button" class="student-map-auto-toggle" :aria-label="autoScrollEnabled ? '暂停自动滚动' : '继续自动滚动'" :aria-pressed="autoScrollEnabled" :title="autoScrollEnabled ? '暂停自动滚动；鼠标移入列表时会暂时停止' : '继续自动滚动'" @click="autoScrollEnabled = !autoScrollEnabled">{{ autoScrollEnabled ? '暂停' : '继续' }}</button><button type="button" aria-label="收起地图列表" @click="listOpen = false">−</button></div></header>
        <div v-if="showListFilter" class="student-map-list-filter"><slot name="list-filter" /></div>
        <div ref="listBody" class="student-map-list-body" tabindex="0"><slot name="list" /></div>
      </section>
      <button v-else class="student-map-open-list" type="button" @click="listOpen = true">☰ {{ listTitle }}</button>
      <div class="student-map-right"><slot name="stats" /><section v-if="detailOpen" class="student-map-detail"><div class="student-map-detail-close"><button type="button" aria-label="收起地图详情" @click="detailOpen = false">×</button></div><slot name="detail" /></section><button v-else class="student-map-open-detail" type="button" @click="detailOpen = true">查看详情 ›</button></div>
      <div v-if="dialogOpen" class="student-map-mask" @pointerdown.self.prevent>
          <section ref="dialog" class="student-map-dialog" :class="{ 'student-map-dialog-measures': dialogTitle === '部门政策措施' }" :style="dialogStyle" role="dialog" aria-modal="true" :aria-label="dialogTitle" tabindex="-1" @keydown.esc.stop>
          <header><h3>{{ dialogTitle }}</h3><button class="student-map-dialog-close" aria-label="关闭弹窗" @click="$emit('close-dialog')">×</button></header>
          <div class="student-map-dialog-body"><slot name="dialog" /></div>
          <footer><slot name="dialog-footer"><button class="btn small" @click="$emit('close-dialog')">关闭</button></slot></footer>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import '../assets/student-map-shell.css'
import '../assets/student-map-immersive.css'

let immersiveOwners = 0

export default {
  name: 'StudentMapShell',
  props: { listTitle: String, listDescription: String, dialogOpen: Boolean, dialogTitle: String, showListFilter: Boolean, autoScroll: Boolean, defaultListOpen: { type: Boolean, default: true }, defaultDetailOpen: { type: Boolean, default: true } },
  data () { return { ready: false, listOpen: this.defaultListOpen, detailOpen: this.defaultDetailOpen, overlayStyle: {}, dialogStyle: {}, observer: null, dockObserver: null, autoScrollEnabled: true, listPointerInside: false, listPointerDown: false, listKeyboardFocus: false, listTouchPause: false, pageVisible: true } },
  computed: {
    shouldAutoScroll () { return this.autoScroll && this.autoScrollEnabled && this.ready && this.listOpen && !this.dialogOpen && !this.listPointerInside && !this.listPointerDown && !this.listKeyboardFocus && !this.listTouchPause && this.pageVisible }
  },
  watch: {
    ready (value) { if (value) this.$nextTick(this.observeDock); else this.dockObserver?.disconnect() },
    dialogOpen (value) { if (value) this.$nextTick(() => { this.measure(); this.$refs.dialog?.focus() }) },
    listOpen (value) {
      if (!value) { this.listPointerInside = false; this.listPointerDown = false; this.listKeyboardFocus = false; this.listTouchPause = false; window.clearTimeout(this.touchResumeTimer) }
      else this.$nextTick(() => { this.listPointerInside = !!this.$refs.listPanel?.matches(':hover') })
    },
    shouldAutoScroll (value) { if (value) this.$nextTick(this.startAutoScroll); else this.stopAutoScroll() }
  },
  mounted () {
    immersiveOwners++
    document.body.classList.add('student-training-immersive')
    document.documentElement.classList.add('student-training-immersive')
    window.addEventListener('resize', this.measure)
    window.addEventListener('scroll', this.measure, { passive: true })
    window.addEventListener('pointerup', this.onListPointerEnd)
    window.addEventListener('pointercancel', this.onListPointerEnd)
    window.addEventListener('blur', this.onWindowBlur)
    document.addEventListener('visibilitychange', this.updatePageVisibility)
    this.updatePageVisibility()
    if (window.matchMedia) {
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
      this.onMotionPreference = event => { if (event.matches) this.autoScrollEnabled = false }
      if (this.motionQuery.matches) this.autoScrollEnabled = false
      if (this.motionQuery.addEventListener) this.motionQuery.addEventListener('change', this.onMotionPreference)
      else if (this.motionQuery.addListener) this.motionQuery.addListener(this.onMotionPreference)
    }
    if (window.ResizeObserver) { this.observer = new ResizeObserver(this.measure); this.observer.observe(this.$refs.shell); this.dockObserver = new ResizeObserver(this.measureDock) }
    this.$nextTick(this.measure)
  },
  updated () { if (!this.dockObserver) this.measureDock() },
  beforeDestroy () {
    this.stopAutoScroll()
    window.clearTimeout(this.touchResumeTimer)
    window.removeEventListener('resize', this.measure)
    window.removeEventListener('scroll', this.measure)
    window.removeEventListener('pointerup', this.onListPointerEnd)
    window.removeEventListener('pointercancel', this.onListPointerEnd)
    window.removeEventListener('blur', this.onWindowBlur)
    document.removeEventListener('visibilitychange', this.updatePageVisibility)
    if (this.motionQuery?.removeEventListener) this.motionQuery.removeEventListener('change', this.onMotionPreference)
    else if (this.motionQuery?.removeListener) this.motionQuery.removeListener(this.onMotionPreference)
    this.observer?.disconnect()
    this.dockObserver?.disconnect()
    immersiveOwners = Math.max(0, immersiveOwners - 1)
    if (!immersiveOwners) { document.body.classList.remove('student-training-immersive'); document.documentElement.classList.remove('student-training-immersive'); document.body.style.removeProperty('--student-map-dock-inset') }
  },
  methods: {
    observeDock () { if (!this.$refs.dock) return; this.dockObserver?.observe(this.$refs.dock); this.measureDock() },
    measureDock () {
      const dock = this.$refs.dock, shell = this.$refs.shell
      if (!dock || !shell || this._isBeingDestroyed || this._isDestroyed) return
      const inset = Math.ceil(dock.getBoundingClientRect().height + 24) + 'px'
      if (shell.style.getPropertyValue('--student-map-dock-inset') !== inset) shell.style.setProperty('--student-map-dock-inset', inset)
      if (document.body.style.getPropertyValue('--student-map-dock-inset') !== inset) document.body.style.setProperty('--student-map-dock-inset', inset)
    },
    showList () { this.listOpen = true },
    hideList () { this.listOpen = false },
    showDetail () { this.detailOpen = true },
    hideDetail () { this.detailOpen = false },
    updatePageVisibility () { this.pageVisible = !document.hidden },
    onWindowBlur () { this.listPointerDown = false; this.listPointerInside = false; this.listTouchPause = false },
    onListPointerEnter (event) { if (event.pointerType !== 'touch') this.listPointerInside = true },
    onListPointerLeave (event) { if (event.pointerType !== 'touch') this.listPointerInside = false },
    onListPointerDown (event) {
      this.lastListPointerTime = Date.now()
      this.listKeyboardFocus = false
      this.listPointerId = event.pointerId
      this.listPointerDown = true
      window.clearTimeout(this.touchResumeTimer)
      if (event.pointerType === 'touch') this.listTouchPause = true
    },
    onListPointerEnd (event) {
      if (!this.listPointerDown || event.pointerId !== this.listPointerId) return
      this.listPointerDown = false
      if (this.listTouchPause) this.touchResumeTimer = window.setTimeout(() => { this.listTouchPause = false }, 3000)
    },
    onListFocusIn () { if (Date.now() - (this.lastListPointerTime || 0) > 500) this.listKeyboardFocus = true },
    onListFocusOut (event) { if (!event.currentTarget.contains(event.relatedTarget)) this.listKeyboardFocus = false },
    startAutoScroll () {
      if (!this.shouldAutoScroll || this.autoScrollTimer) return
      this.scrollPosition = this.$refs.listBody?.scrollTop || 0
      this.scrollDirection = this.scrollDirection || 1
      this.scrollLastTime = Date.now()
      this.autoScrollTimer = window.setInterval(this.stepAutoScroll, 50)
    },
    stopAutoScroll () { if (this.autoScrollTimer) window.clearInterval(this.autoScrollTimer); this.autoScrollTimer = null },
    holdListAutoScroll (duration = 3000) { this.scrollPosition = null; this.scrollHoldUntil = Date.now() + duration },
    resetListAutoScroll () { this.scrollPosition = null; this.scrollDirection = 1; this.scrollHoldUntil = Date.now() + 1000 },
    stepAutoScroll () {
      const now = Date.now(), elapsed = Math.min(100, now - this.scrollLastTime)
      this.scrollLastTime = now
      const list = this.$refs.listBody
      if (!list || now < (this.scrollHoldUntil || 0)) return
      const max = Math.max(0, list.scrollHeight - list.clientHeight)
      if (!max) { this.scrollPosition = 0; this.scrollDirection = 1; return }
      if (this.scrollPosition == null || Math.abs(list.scrollTop - this.scrollPosition) > 1) this.scrollPosition = list.scrollTop
      const next = this.scrollPosition + this.scrollDirection * elapsed * 0.025
      if (next >= max || next <= 0) {
        this.scrollPosition = Math.max(0, Math.min(max, next))
        this.scrollDirection = next >= max ? -1 : 1
        this.scrollHoldUntil = now + 1200
      } else this.scrollPosition = next
      list.scrollTop = this.scrollPosition
    },
    measure () { this.$nextTick(() => { const shell = this.$refs.shell; const viewport = shell?.querySelector('.training-map-viewport, .policy-city-viewport'); if (!shell || !viewport) return; const a = shell.getBoundingClientRect(), b = viewport.getBoundingClientRect(); this.overlayStyle = { top: (b.top - a.top) + 'px', left: (b.left - a.left) + 'px', width: b.width + 'px', height: b.height + 'px' }; const top = Math.max(0, -b.top), bottom = Math.min(b.height, window.innerHeight - b.top); if (bottom > top) this.dialogStyle = { top: ((top + bottom) / 2) + 'px', maxHeight: Math.max(80, bottom - top - 24) + 'px' }; this.ready = b.width > 0 && b.height > 0; this.$nextTick(this.measureDock) }) }
  }
}
</script>

